import React, { useState } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BankIcon,
  BuildingIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  EditIcon,
  ExternalLinkIcon,
  EyeIcon,
  FileIcon,
  FolderIcon,
  InfoIcon,
  MailIcon,
  MapPinIcon,
  PhoneIcon,
  SaveIcon,
  SendIcon,
  ShieldCheckIcon,
  StarIcon,
  UploadIcon,
  UserIcon,
  UsersIcon,
} from "../../Components/Icons";
import {
  ACCOUNTING_METHODS,
  APPLICATION,
  BANKS,
  BRANCHES,
  BUSINESS_ACTIVITIES,
  CAE_CODES,
  CAPITAL_BANDS,
  CHECKLIST,
  COMMUNES,
  COUNTRIES,
  DISTRICTS,
  EMPLOYEE_BANDS,
  FISCAL_YEAR_ENDS,
  GUIDELINES,
  INCOME_SOURCES,
  INITIAL_FORM,
  LEGAL_NATURES,
  LEGAL_STRUCTURES,
  MONTHS,
  MUNICIPALITIES,
  PROVINCES,
  RECENT_APPLICATIONS,
  REQUIRED,
  STEPS,
  TAXPAYER_TYPES,
  TAX_REGIMES,
  TURNOVER_BANDS,
} from "./data";
import "./NifRegistration.css";

const TYPE_ICONS = {
  building: BuildingIcon,
  user: UserIcon,
  users: UsersIcon,
  bank: BankIcon,
};

const GUIDE_ICONS = {
  star: StarIcon,
  clock: ClockIcon,
  shield: ShieldCheckIcon,
  info: InfoIcon,
  mail: MailIcon,
};

const chipClass = (value) =>
  `chip chip--${value.toLowerCase().replace(/\s+/g, "-")}`;

/* -------------------------------------------------------------- fields -- */

function Field({ label, required, optional, error, children, wide }) {
  return (
    <div className={`fld${wide ? " fld--wide" : ""}`}>
      <span className="fld__label">
        {label}
        {required && <b aria-hidden="true"> *</b>}
        {optional && <span className="fld__optional"> (Optional)</span>}
      </span>
      {children}
      {error && <span className="fld__error">{error}</span>}
    </div>
  );
}

function Select({ value, onChange, options, placeholder, invalid }) {
  return (
    <span className={`select select--block${invalid ? " select--invalid" : ""}`}>
      <select value={value} onChange={onChange}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <ChevronDownIcon size={16} />
    </span>
  );
}

function Radio({ name, value, checked, onChange, label }) {
  return (
    <label className="radio">
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
      />
      {label}
    </label>
  );
}

export default function NifRegistration() {
  const [step, setStep] = useState(0);
  const [furthest, setFurthest] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  const stepKey = STEPS[step].key;
  const selectedType =
    TAXPAYER_TYPES.find((item) => item.key === form.taxpayerType) ??
    TAXPAYER_TYPES[0];

  const set = (name) => (event) => {
    const { value, type, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const goTo = (index) => {
    setStep(index);
    setFurthest((prev) => Math.max(prev, index));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const next = () => {
    const required = REQUIRED[stepKey] ?? [];
    const missing = {};
    required.forEach((name) => {
      if (!String(form[name] ?? "").trim()) missing[name] = "This field is required";
    });

    if (Object.keys(missing).length) {
      setErrors(missing);
      return;
    }
    goTo(Math.min(step + 1, STEPS.length - 1));
  };

  const err = (name) => errors[name];

  /* ------------------------------------------------------------ steps -- */

  const renderTypeStep = () => (
    <>
      <section className="dgi-card">
        <h2 className="wiz__title">Select Taxpayer Type</h2>
        <p className="wiz__lede">Choose the type of taxpayer you want to register.</p>

        <div className="row g-3 wiz__types">
          {TAXPAYER_TYPES.map((item) => {
            const Icon = TYPE_ICONS[item.icon];
            const active = form.taxpayerType === item.key;
            return (
              <div className="col-6 col-xl-3" key={item.key}>
                <button
                  type="button"
                  className={`type-card${active ? " type-card--active" : ""}`}
                  aria-pressed={active}
                  onClick={() =>
                    setForm((prev) => ({ ...prev, taxpayerType: item.key }))
                  }
                >
                  {active && (
                    <span className="type-card__check">
                      <CheckCircleIcon size={20} />
                    </span>
                  )}
                  <span className={`type-card__icon kpi__icon--${item.tone}`}>
                    <Icon size={28} />
                  </span>
                  <span className="type-card__label">{item.label}</span>
                  <span className="type-card__text">{item.text}</span>
                </button>
              </div>
            );
          })}
        </div>

        <div className="wiz__note">
          <InfoIcon size={18} />
          <div>
            <p className="wiz__note-title">{selectedType.label}</p>
            <p className="wiz__note-text">{selectedType.detail}</p>
            <p className="wiz__note-text">
              Required documents: {selectedType.documents}
            </p>
          </div>
        </div>

        <div className="wiz__foot wiz__foot--end">
          <button type="button" className="dgi-btn dgi-btn--primary" onClick={next}>
            Next <ArrowRightIcon size={16} />
          </button>
        </div>
      </section>

      <section className="dgi-card">
        <h2 className="wiz__title wiz__title--sm">Recent NIF Applications</h2>
        <div className="dgi-table-wrap">
          <table className="dgi-table dgi-table--apps">
            <thead>
              <tr>
                <th>Application ID</th>
                <th>Taxpayer Name</th>
                <th>Type</th>
                <th>Date Created</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {RECENT_APPLICATIONS.map((app) => (
                <tr key={app.id}>
                  <td>{app.id}</td>
                  <td>{app.name}</td>
                  <td>{app.type}</td>
                  <td>{app.created}</td>
                  <td>
                    <span className={chipClass(app.status)}>{app.status}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="row-actions__btn"
                      aria-label={`View ${app.id}`}
                    >
                      <EyeIcon size={17} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button type="button" className="dgi-card__link">
          View all applications <ArrowRightIcon size={16} />
        </button>
      </section>
    </>
  );

  const renderBasicStep = () => (
    <section className="dgi-card">
      <h2 className="wiz__title">Basic Information</h2>
      <p className="wiz__lede">Enter the basic details of the taxpayer.</p>

      <div className="fld-grid">
        <Field label="Taxpayer Type">
          <span className="fld-static">
            <BuildingIcon size={17} />
            {selectedType.label}
          </span>
        </Field>

        <Field label="Legal Structure" required error={err("legalStructure")}>
          <Select
            value={form.legalStructure}
            onChange={set("legalStructure")}
            options={LEGAL_STRUCTURES}
            placeholder="Select structure"
            invalid={!!err("legalStructure")}
          />
        </Field>

        <Field label="Company Name" required error={err("companyName")}>
          <input
            className="fld__input"
            value={form.companyName}
            onChange={set("companyName")}
          />
        </Field>

        <Field label="Trade Name (If any)">
          <input
            className="fld__input"
            value={form.tradeName}
            onChange={set("tradeName")}
          />
        </Field>

        <Field
          label="Registration Number (Commercial)"
          required
          error={err("registrationNumber")}
        >
          <input
            className="fld__input"
            value={form.registrationNumber}
            onChange={set("registrationNumber")}
          />
        </Field>

        <Field label="Incorporation Date" required error={err("incorporationDate")}>
          <input
            type="date"
            className="fld__input"
            value={form.incorporationDate}
            onChange={set("incorporationDate")}
          />
        </Field>

        <Field label="Fiscal Year End">
          <Select
            value={form.fiscalYearEnd}
            onChange={set("fiscalYearEnd")}
            options={FISCAL_YEAR_ENDS}
          />
        </Field>

        <Field label="Primary Business Activity" required error={err("primaryActivity")}>
          <Select
            value={form.primaryActivity}
            onChange={set("primaryActivity")}
            options={BUSINESS_ACTIVITIES}
            placeholder="Select activity"
            invalid={!!err("primaryActivity")}
          />
        </Field>

        <Field label="Secondary Business Activity" optional>
          <Select
            value={form.secondaryActivity}
            onChange={set("secondaryActivity")}
            options={BUSINESS_ACTIVITIES}
            placeholder="Select activity"
          />
        </Field>

        <Field
          label="Country of Incorporation"
          required
          error={err("countryIncorporation")}
        >
          <Select
            value={form.countryIncorporation}
            onChange={set("countryIncorporation")}
            options={COUNTRIES}
          />
        </Field>

        <Field label="Country of Operation" required error={err("countryOperation")}>
          <Select
            value={form.countryOperation}
            onChange={set("countryOperation")}
            options={COUNTRIES}
          />
        </Field>

        <Field label="Telephone Number" required error={err("telephone")}>
          <span className="fld__phone">
            <span className="fld__flag">🇦🇴</span>
            <input
              className="fld__input"
              value={form.telephone}
              onChange={set("telephone")}
            />
          </span>
        </Field>

        <Field label="Email Address" required error={err("email")}>
          <input
            type="email"
            className="fld__input"
            value={form.email}
            onChange={set("email")}
          />
        </Field>

        <Field label="Website (If any)">
          <input
            className="fld__input"
            value={form.website}
            onChange={set("website")}
          />
        </Field>
      </div>

      <Field
        label="Short Description of Business"
        required
        error={err("description")}
        wide
      >
        <textarea
          className="fld__input fld__textarea"
          rows={3}
          maxLength={500}
          value={form.description}
          onChange={set("description")}
        />
        <span className="fld__count">{form.description.length} / 500</span>
      </Field>

      {renderNav()}
    </section>
  );

  const renderAddressStep = () => (
    <section className="dgi-card">
      <h2 className="wiz__title">Address</h2>
      <p className="wiz__lede">Enter the registered address of the taxpayer.</p>

      <span className="fld__label">Address Type</span>
      <div className="addr-types">
        {[
          {
            key: "registered",
            label: "Registered Address",
            text: "Official address for legal and tax purposes",
          },
          {
            key: "correspondence",
            label: "Correspondence Address",
            text: "Address for all official communications",
          },
        ].map((option) => (
          <label
            key={option.key}
            className={`addr-type${
              form.addressType === option.key ? " addr-type--active" : ""
            }`}
          >
            <input
              type="radio"
              name="addressType"
              value={option.key}
              checked={form.addressType === option.key}
              onChange={set("addressType")}
            />
            <span>
              <span className="addr-type__label">{option.label}</span>
              <span className="addr-type__text">{option.text}</span>
            </span>
          </label>
        ))}
      </div>

      <div className="fld-grid">
        <Field label="Country" required error={err("country")}>
          <Select value={form.country} onChange={set("country")} options={COUNTRIES} />
        </Field>
        <Field label="Province" required error={err("province")}>
          <Select value={form.province} onChange={set("province")} options={PROVINCES} />
        </Field>
        <Field label="Municipality" required error={err("municipality")}>
          <Select
            value={form.municipality}
            onChange={set("municipality")}
            options={MUNICIPALITIES}
          />
        </Field>
        <Field label="District" required error={err("district")}>
          <Select value={form.district} onChange={set("district")} options={DISTRICTS} />
        </Field>
        <Field label="Commune" required error={err("commune")}>
          <Select value={form.commune} onChange={set("commune")} options={COMMUNES} />
        </Field>
        <Field label="Neighborhood/Area" required error={err("neighborhood")}>
          <input
            className="fld__input"
            value={form.neighborhood}
            onChange={set("neighborhood")}
          />
        </Field>
        <Field label="Street / Avenue" required error={err("street")}>
          <input className="fld__input" value={form.street} onChange={set("street")} />
        </Field>
        <Field label="Building / House No." required error={err("building")}>
          <input
            className="fld__input"
            value={form.building}
            onChange={set("building")}
          />
        </Field>
        <Field label="Floor" optional>
          <input className="fld__input" value={form.floor} onChange={set("floor")} />
        </Field>
        <Field label="Apartment / Suite" optional>
          <input
            className="fld__input"
            value={form.apartment}
            onChange={set("apartment")}
          />
        </Field>
        <Field label="P.O. Box" optional>
          <input className="fld__input" value={form.poBox} onChange={set("poBox")} />
        </Field>
        <Field label="Postal Code" optional>
          <input
            className="fld__input"
            value={form.postalCode}
            onChange={set("postalCode")}
          />
        </Field>
      </div>

      <div className="fld-grid fld-grid--half">
        <Field label="Landmark" optional>
          <input
            className="fld__input"
            value={form.landmark}
            onChange={set("landmark")}
          />
        </Field>
        <Field label="Address Description" optional>
          <textarea
            className="fld__input fld__textarea"
            rows={2}
            placeholder="Any additional address information"
            value={form.addressDescription}
            onChange={set("addressDescription")}
          />
        </Field>
      </div>

      {renderNav()}
    </section>
  );

  const renderOtherStep = () => (
    <section className="dgi-card">
      <h2 className="wiz__title">Other Information</h2>
      <p className="wiz__lede">
        Provide additional information about the taxpayer.
      </p>

      <h3 className="wiz__group">Business Details</h3>
      <div className="fld-grid">
        <Field label="Date Business Started">
          <input
            type="date"
            className="fld__input"
            value={form.businessStarted}
            onChange={set("businessStarted")}
          />
        </Field>
        <Field label="Legal Nature of Business" required error={err("legalNature")}>
          <Select
            value={form.legalNature}
            onChange={set("legalNature")}
            options={LEGAL_NATURES}
            invalid={!!err("legalNature")}
          />
        </Field>
        <Field label="Number of Employees">
          <Select
            value={form.employees}
            onChange={set("employees")}
            options={EMPLOYEE_BANDS}
          />
        </Field>
        <Field label="Main Source of Income" required error={err("incomeSource")}>
          <Select
            value={form.incomeSource}
            onChange={set("incomeSource")}
            options={INCOME_SOURCES}
            invalid={!!err("incomeSource")}
          />
        </Field>
        <Field label="Annual Turnover (Estimated)">
          <Select
            value={form.turnover}
            onChange={set("turnover")}
            options={TURNOVER_BANDS}
          />
        </Field>
        <Field label="Capital (Paid-up)">
          <Select
            value={form.capital}
            onChange={set("capital")}
            options={CAPITAL_BANDS}
          />
        </Field>
      </div>

      <h3 className="wiz__group">Tax Information</h3>
      <div className="fld-grid">
        <Field label="Tax Regime" required error={err("taxRegime")}>
          <Select
            value={form.taxRegime}
            onChange={set("taxRegime")}
            options={TAX_REGIMES}
            invalid={!!err("taxRegime")}
          />
        </Field>
        <Field label="VAT Registration Required" required>
          <div className="radio-row">
            <Radio
              name="vatRequired"
              value="yes"
              checked={form.vatRequired === "yes"}
              onChange={set("vatRequired")}
              label="Yes"
            />
            <Radio
              name="vatRequired"
              value="no"
              checked={form.vatRequired === "no"}
              onChange={set("vatRequired")}
              label="No"
            />
          </div>
        </Field>
        <Field label="Excise Applicable">
          <div className="radio-row">
            <Radio
              name="exciseApplicable"
              value="yes"
              checked={form.exciseApplicable === "yes"}
              onChange={set("exciseApplicable")}
              label="Yes"
            />
            <Radio
              name="exciseApplicable"
              value="no"
              checked={form.exciseApplicable === "no"}
              onChange={set("exciseApplicable")}
              label="No"
            />
          </div>
        </Field>
        <Field label="Accounting Method" required error={err("accountingMethod")}>
          <Select
            value={form.accountingMethod}
            onChange={set("accountingMethod")}
            options={ACCOUNTING_METHODS}
            invalid={!!err("accountingMethod")}
          />
        </Field>
        <Field
          label="Financial Year Start Month"
          required
          error={err("financialYearStart")}
        >
          <Select
            value={form.financialYearStart}
            onChange={set("financialYearStart")}
            options={MONTHS}
            invalid={!!err("financialYearStart")}
          />
        </Field>
      </div>

      <h3 className="wiz__group">Banking Information</h3>
      <div className="fld-grid">
        <Field label="Bank Name">
          <Select value={form.bankName} onChange={set("bankName")} options={BANKS} />
        </Field>
        <Field label="Branch">
          <Select value={form.branch} onChange={set("branch")} options={BRANCHES} />
        </Field>
        <Field label="IBAN" optional>
          <input className="fld__input" value={form.iban} onChange={set("iban")} />
        </Field>
      </div>

      <h3 className="wiz__group">Additional Information</h3>
      <div className="fld-grid">
        <Field label="Economic Activity Code (CAE)" required error={err("caeCode")}>
          <Select
            value={form.caeCode}
            onChange={set("caeCode")}
            options={CAE_CODES}
            invalid={!!err("caeCode")}
          />
        </Field>
        <Field label="Business Website" optional>
          <input
            className="fld__input"
            value={form.businessWebsite}
            onChange={set("businessWebsite")}
          />
        </Field>
        <Field label="Additional Notes" optional>
          <textarea
            className="fld__input fld__textarea"
            rows={3}
            maxLength={500}
            value={form.notes}
            onChange={set("notes")}
          />
          <span className="fld__count">{form.notes.length} / 500</span>
        </Field>
      </div>

      {renderNav()}
    </section>
  );

  const summaryRows = [
    {
      key: "basic",
      step: 1,
      icon: BankIcon,
      title: "Basic Information",
      parts: [
        selectedType.label,
        form.legalStructure,
        form.companyName,
        form.registrationNumber,
      ],
    },
    {
      key: "address",
      step: 2,
      icon: MapPinIcon,
      title: "Address",
      parts: [
        [
          form.street,
          form.building,
          form.apartment,
          form.neighborhood,
          form.district,
          form.province,
          form.country,
        ]
          .filter(Boolean)
          .join(", "),
      ],
    },
    {
      key: "other",
      step: 3,
      icon: FolderIcon,
      title: "Other Information",
      parts: [
        form.incomeSource,
        form.taxRegime,
        `${form.employees} Employees`,
        `Annual Turnover: ${form.turnover}`,
      ],
    },
  ];

  const renderReviewStep = () => (
    <section className="dgi-card">
      <div className="wiz__review-head">
        <div>
          <h2 className="wiz__title">Review &amp; Submit</h2>
          <p className="wiz__lede">
            Please review all information below before submitting your
            application.
          </p>
        </div>
        <button
          type="button"
          className="dgi-btn dgi-btn--ghost dgi-btn--sm"
          onClick={() => goTo(1)}
        >
          <EditIcon size={16} />
          Edit All Sections
        </button>
      </div>

      {summaryRows.map((row) => {
        const Icon = row.icon;
        return (
          <div className="summary" key={row.key}>
            <span className="summary__icon">
              <Icon size={20} />
            </span>
            <div className="summary__body">
              <p className="summary__title">{row.title}</p>
              <p className="summary__parts">
                {row.parts.filter(Boolean).map((part, index) => (
                  <span key={part}>
                    {index > 0 && <i className="summary__dot" />}
                    {part}
                  </span>
                ))}
              </p>
            </div>
            <span className="summary__actions">
              <button
                type="button"
                className="dgi-btn dgi-btn--ghost dgi-btn--sm"
                onClick={() => goTo(row.step)}
              >
                <EditIcon size={15} />
                Edit
              </button>
              <button
                type="button"
                className="summary__more"
                aria-label={`Expand ${row.title}`}
              >
                <ChevronDownIcon size={16} />
              </button>
            </span>
          </div>
        );
      })}

      <div className="summary">
        <span className="summary__icon">
          <FileIcon size={20} />
        </span>
        <div className="summary__body">
          <p className="summary__title">Documents (0)</p>
          <p className="summary__parts">
            <span>No documents uploaded yet</span>
          </p>
        </div>
        <span className="summary__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost dgi-btn--sm">
            <UploadIcon size={15} />
            Upload Documents
          </button>
          <button type="button" className="summary__more" aria-label="Expand documents">
            <ChevronDownIcon size={16} />
          </button>
        </span>
      </div>

      <div className="declaration">
        <p className="declaration__title">
          <ShieldCheckIcon size={18} />
          Declaration
        </p>
        <p className="declaration__text">
          I hereby declare that the information provided in this application is
          true, complete and correct to the best of my knowledge.
          <br />I understand that providing false information may result in
          penalties as per applicable tax laws.
        </p>
        <label className="declaration__agree">
          <input
            type="checkbox"
            checked={form.declaration}
            onChange={set("declaration")}
          />
          I agree to the above declaration.
        </label>
      </div>

      <div className="wiz__foot">
        <button
          type="button"
          className="dgi-btn dgi-btn--ghost"
          onClick={() => goTo(step - 1)}
        >
          <ArrowLeftIcon size={16} />
          Previous
        </button>

        <div className="wiz__submit">
          <button
            type="button"
            className="dgi-btn dgi-btn--merge"
            disabled={!form.declaration}
          >
            Submit Application
            <SendIcon size={16} />
          </button>
          <p className="wiz__submit-note">
            <ShieldCheckIcon size={14} />
            Your application will be reviewed by DGI officials.
          </p>
        </div>
      </div>
    </section>
  );

  function renderNav() {
    return (
      <>
        <div className="wiz__foot">
          <button
            type="button"
            className="dgi-btn dgi-btn--ghost"
            onClick={() => goTo(step - 1)}
          >
            <ArrowLeftIcon size={16} />
            Previous
          </button>
          <button type="button" className="dgi-btn dgi-btn--primary" onClick={next}>
            Next <ArrowRightIcon size={16} />
          </button>
        </div>
        <p className="wiz__required">
          Fields marked with <b>*</b> are mandatory.
        </p>
      </>
    );
  }

  const STEP_VIEWS = [
    renderTypeStep,
    renderBasicStep,
    renderAddressStep,
    renderOtherStep,
    renderReviewStep,
  ];

  return (
    <div className="page">
      {/* --------------------------------------------------------- head -- */}
      <div className="page__head">
        <div>
          <h1 className="page__title">NIF Registration</h1>
          <p className="page__subtitle">
            Register a new taxpayer to issue a National Identification Number
            (NIF).
          </p>
        </div>

        <div className="page__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <ArrowLeftIcon size={16} />
            Back to List
          </button>
          <button type="button" className="dgi-btn dgi-btn--primary">
            <SaveIcon size={17} />
            Save as Draft
          </button>
        </div>
      </div>

      {/* ------------------------------------------------------ stepper -- */}
      <nav className="dgi-card stepper" aria-label="Registration steps">
        {STEPS.map((item, index) => {
          const done = index < step;
          const current = index === step;
          const reachable = index <= furthest;

          return (
            <React.Fragment key={item.key}>
              {index > 0 && <span className="stepper__gap" aria-hidden="true" />}
              <button
                type="button"
                className={`stepper__step${
                  current ? " stepper__step--current" : ""
                }${done ? " stepper__step--done" : ""}`}
                onClick={() => reachable && goTo(index)}
                disabled={!reachable}
                aria-current={current ? "step" : undefined}
              >
                <span className="stepper__badge">
                  {done ? <CheckCircleIcon size={18} /> : index + 1}
                </span>
                <span className="stepper__meta">
                  <span className="stepper__title">{item.title}</span>
                  <span className="stepper__hint">{item.hint}</span>
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </nav>

      {/* ------------------------------------------------ form + sidebar -- */}
      <div className="wiz">
        <div className="stack">{STEP_VIEWS[step]()}</div>

        <div className="stack">
          <section className="dgi-card">
            <h2 className="wiz__side-title">
              {stepKey === "review"
                ? "Registration Summary"
                : "Registration Information"}
            </h2>
            <dl className="kv kv--tight">
              <div className="kv__row">
                <dt>Application ID</dt>
                <dd>{step === 0 || step === 1 ? "—" : APPLICATION.id}</dd>
              </div>
              <div className="kv__row">
                <dt>Application Date</dt>
                <dd>{APPLICATION.date}</dd>
              </div>
              <div className="kv__row">
                <dt>Application Status</dt>
                <dd>
                  <span className={chipClass(APPLICATION.status)}>
                    {APPLICATION.status}
                  </span>
                </dd>
              </div>
              <div className="kv__row">
                <dt>Created By</dt>
                <dd>{APPLICATION.createdBy}</dd>
              </div>
              <div className="kv__row">
                <dt>Last Updated</dt>
                <dd>{APPLICATION.updated}</dd>
              </div>
            </dl>
          </section>

          {stepKey === "review" && (
            <section className="dgi-card">
              <h2 className="wiz__side-title">Checklist</h2>
              <ul className="checklist">
                {CHECKLIST.map((item) => (
                  <li key={item}>
                    <CheckCircleIcon size={16} />
                    {item}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="dgi-card">
            <h2 className="wiz__side-title">Guidelines</h2>
            <ul className="guides">
              {GUIDELINES[stepKey].map((guide) => {
                const Icon = GUIDE_ICONS[guide.icon];
                return (
                  <li key={guide.text}>
                    <Icon size={16} />
                    {guide.text}
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="dgi-card">
            <h2 className="wiz__side-title">Need Help?</h2>
            <p className="help__lede">
              If you have any questions, please contact our support team.
            </p>
            <ul className="help">
              <li>
                <PhoneIcon size={16} />
                +244 222 70 60 00
              </li>
              <li>
                <MailIcon size={16} />
                <a className="link" href="mailto:suporte@dgi.gov.ao">
                  suporte@dgi.gov.ao
                </a>
              </li>
            </ul>
            <button type="button" className="link help__center">
              Help Center <ExternalLinkIcon size={14} />
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}
